/**
 * Estado de sessão do usuário — token JWT persistido em localStorage. // ux-neutrality:allow doc-comment
 *
 * Por que Zustand + persist em vez de Context: // ux-neutrality:allow doc-comment
 *   - escolha reativa global sem prop-drilling pelos componentes do Shell. // ux-neutrality:allow doc-comment
 *   - persistência em localStorage transparente, com chave única do projeto. // ux-neutrality:allow doc-comment
 *   - selectors permitem componentes re-renderizarem só quando o slice deles // ux-neutrality:allow doc-comment
 *     muda (AppHeader vê apenas `token != null`; LoginPage chama `setToken`). // ux-neutrality:allow doc-comment
 *
 * Sobre o conteúdo do JWT: este store **não decodifica** o token — apenas // ux-neutrality:allow doc-comment
 * persiste a string opaca. Identidade do usuário vem do endpoint /api/users/me // ux-neutrality:allow doc-comment
 * (carregado sob demanda), nunca do payload do JWT no cliente. // ux-neutrality:allow doc-comment
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const SESSION_STORAGE_KEY = 'signcheck.session';

// Slice é JWT (RFC 7519) — convenção HTTP, sem string de UI. // ux-neutrality:allow auth-token
export interface SessionState {
  token: string | null; // ux-neutrality:allow auth-token (JWT, RFC 7519)
  /** Flag transitória: sessão expirou no servidor (resposta 401). // ux-neutrality:allow doc-comment
   *  Não é persistida — sumir após reload é desejado. */
  sessionExpired: boolean;
  /** Banco em cujo contexto a sessão opera (E57.F2, ADR-0066).
   *
   *  Persistido junto com a sessão para sobreviver ao reload — trocar de aba
   *  não pode reabrir a escolha. O valor é uma PROPOSTA: quem decide é o
   *  servidor, que confere contra o vínculo do escritório e recusa com 403 o
   *  que não for dele. */
  bancoAtivo: string | null;
  setToken: (token: string) => void; // ux-neutrality:allow auth-token
  clearToken: () => void; // ux-neutrality:allow auth-token
  setSessionExpired: (value: boolean) => void;
  setBancoAtivo: (bancoId: string | null) => void;
}

/** O que sobrevive ao reload: credencial e contexto de banco (E57.F2). */
type SessionPersistido = Pick<SessionState, 'token' | 'bancoAtivo'>; // ux-neutrality:allow auth-token

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      token: null, // ux-neutrality:allow auth-token
      sessionExpired: false,
      bancoAtivo: null,
      setToken: (token: string) => set({ token }), // ux-neutrality:allow auth-token
      // Sair leva o contexto junto: sessão nova não herda a carteira da antiga.
      clearToken: () => set({ token: null, bancoAtivo: null }), // ux-neutrality:allow auth-token
      setSessionExpired: (value: boolean) => set({ sessionExpired: value }),
      setBancoAtivo: (bancoId: string | null) => set({ bancoAtivo: bancoId }),
    }),
    {
      name: SESSION_STORAGE_KEY,
      // Persistir apenas o token; `sessionExpired` é transitório (não sobrevive a reload). // ux-neutrality:allow auth-token
      partialize: (state) =>
        // ux-neutrality:allow auth-token
        ({ token: state.token, bancoAtivo: state.bancoAtivo }) as SessionPersistido, // ux-neutrality:allow auth-token
    },
  ),
);

export function useIsAuthenticated(): boolean {
  return useSessionStore((s) => s.token !== null); // ux-neutrality:allow auth-token
}

export function useBancoAtivo(): string | null {
  return useSessionStore((s) => s.bancoAtivo);
}

export function useToken(): string | null {
  // ux-neutrality:allow auth-token
  return useSessionStore((s) => s.token); // ux-neutrality:allow auth-token
}
